// ImageComponent.js
import React, { useEffect } from 'react';

import { listImages, deleteImage } from '../services/api';
import { DataState } from '../context/DataProvider';

const ImageComponent = ({
  tempImages,
  justUploadedImages,
  setJustUploadedImages,
}) => {
  const {
    currentTaskId: taskId,
    userGoogleId,
    currentTaskImages: fetchedImages,
    setCurrentTaskImages: setFetchedImages,
  } = DataState();

  const fetchImages = async () => {
    if (!userGoogleId) return;

    try {
      const data = await listImages(userGoogleId, taskId);
      if (data.error) {
        return;
      }
      const formattedImages = data.map((img) => ({
        id: img.file_id,
        ...img,
        uploading: false,
      }));
      setFetchedImages(formattedImages);
    } catch (error) {
    } finally {
      if (justUploadedImages) {
        setJustUploadedImages(false);
      }
    }
  };

  useEffect(() => {
    if (!userGoogleId) return;

    if (justUploadedImages) {
      fetchImages();
      setJustUploadedImages(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [justUploadedImages]);

  // useEffect(() => {
  //   if (!userGoogleId) return;
  //   fetchImages();
  // }, [taskId, userGoogleId]);

  // Combine temporary (uploading) images with the fetched final images.
  const allImages = [...fetchedImages, ...tempImages];

  // Handle deletion: mark as deleting, then call the backend endpoint.
  const handleDelete = async (id) => {
    // Mark image as deleting in the fetched images state.
    setFetchedImages((prevImages) =>
      prevImages.map((img) =>
        img.id === id ? { ...img, deleting: true } : img,
      ),
    );
    try {
      // Call the actual backend delete API.
      await deleteImage(id, userGoogleId);
      // Remove the image with the given id.
      setFetchedImages((prevImages) =>
        prevImages.filter((img) => img.id !== id),
      );
    } catch (error) {
      // Optionally, reset the deleting flag if the deletion fails.
      setFetchedImages((prevImages) =>
        prevImages.map((img) =>
          img.id === id ? { ...img, deleting: false } : img,
        ),
      );
    } finally {
      setJustUploadedImages(true);
    }
  };

  // Handle download: mark as downloading, then open the URL.
  const handleDownload = (id) => {
    const image = allImages.find((img) => img.id === id);
    if (!image) return;
    // Here we update the fetchedImages state for downloaded images;
    // you can adjust if you need to update tempImages as well.
    setFetchedImages((prevImages) =>
      prevImages.map((img) =>
        img.id === id ? { ...img, downloading: true } : img,
      ),
    );
    window.open(image.url, '_blank');
    setFetchedImages((prevImages) =>
      prevImages.map((img) =>
        img.id === id ? { ...img, downloading: false } : img,
      ),
    );
  };
  // eslint-disable-next-line
  if (allImages.length == 0) return null;

  return (
    <div className="font-sans">
      {/* Custom CSS for animations and scrollbar styling */}
      <style>{`
        /* Upload scanning animation: moves from bottom to top */
        @keyframes scan {
          0% { transform: translateY(100%); }
          50% { transform: translateY(0%); }
          100% { transform: translateY(-100%); }
        }
        .animate-scan {
          animation: scan 1s linear infinite;
        }
        /* Delete scanning animation: moves from top to bottom */
        @keyframes deleteScan {
          0% { transform: translateY(-100%); }
          50% { transform: translateY(0%); }
          100% { transform: translateY(100%); }
        }
        .animate-delete-scan {
          animation: deleteScan 1s linear infinite;
        }
        /* WebKit scrollbar styling */
        .image-scroll-container::-webkit-scrollbar {
          height: 6px;
        }
        .image-scroll-container::-webkit-scrollbar-track {
          background: transparent;
        }
        .image-scroll-container::-webkit-scrollbar-thumb {
          background-color: rgba(62, 62, 62, 0.3);
          border-radius: 3px;
        }
        /* Firefox scrollbar styling */
        .image-scroll-container {
          scrollbar-width: thin;
          scrollbar-color: rgba(58, 58, 58, 0.3) transparent;
        }
      `}</style>

      {/* Horizontally scrollable images container */}
      <div className="image-scroll-container overflow-x-auto overflow-y-hidden whitespace-nowrap pl-2 pr-2 pt-3">
        {allImages.map((image) => (
          <div
            key={image.id}
            className="group relative mr-2.5 inline-block align-bottom transition-all duration-300"
          >
            <img
              src={image.url}
              alt="Pasted"
              className="block max-h-[150px] max-w-[150px]"
            />

            {/* Upload scanning overlay */}
            {image.uploading && (
              <div className="pointer-events-none absolute inset-0 bg-white bg-opacity-40">
                <div className="animate-scan absolute inset-0 bg-gradient-to-t from-transparent via-white/50 to-transparent" />
              </div>
            )}

            {/* Deletion scanning overlay */}
            {image.deleting && (
              <div className="pointer-events-none absolute inset-0 bg-red-500 bg-opacity-40">
                <div className="animate-delete-scan absolute inset-0 bg-gradient-to-b from-transparent via-red-200/50 to-transparent" />
              </div>
            )}

            {/* Download overlay: appears on hover when not uploading or deleting */}
            {!image.uploading && !image.deleting && (
              <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                <button
                  onClick={() => handleDownload(image.id)}
                  disabled={image.downloading}
                  className="rounded-full bg-white p-2 shadow-lg"
                >
                  {image.downloading ? (
                    // Animated icon while downloading.
                    <svg
                      className="h-6 w-6 animate-spin text-[#5652CF]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
                      />
                    </svg>
                  ) : (
                    // Standard download icon.
                    <svg
                      className="h-6 w-6 text-[#5652CF]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
                      />
                    </svg>
                  )}
                </button>
              </div>
            )}

            {/* Delete button (always visible in top-right corner) */}
            <button
              onClick={() => handleDelete(image.id)}
              title="Remove Image"
              className="absolute right-0 top-0 h-5 w-5 -translate-y-1/2 translate-x-1/2 rounded-full bg-white bg-opacity-80 p-0 text-center font-bold leading-5"
              disabled={image.deleting}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ImageComponent;
